import { createContext, useContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

/**
 * Enhanced QR Configuration with advanced design options
 * Supports gradients, metallic colors, and shapes
 */
export const DEFAULT_QR_CONFIG = {
  // Basic data
  data: '',
  width: 300,
  height: 300,
  margin: 10,
  
  // Dot/Module options
  dotsType: 'rounded',
  dotsColor: '#000000',
  dotsGradient: null, // { type: 'linear'|'radial', colors: [{offset, color}], rotation }
  
  // Corner square options
  cornersSquareType: 'extra-rounded',
  cornersSquareColor: '#000000',
  cornersSquareGradient: null,
  
  // Corner dot options
  cornersDotType: 'dot',
  cornersDotColor: '#000000',
  cornersDotGradient: null,
  
  // Background options
  backgroundColor: '#ffffff',
  backgroundGradient: null,
  transparentBackground: false, // NEW: Enable transparent background export
  
  // Logo/Center Image
  image: null,
  imageSize: 0.4,
  imageMargin: 5,
  
  // Shape - only 'square' and 'circle' actually work with qr-code-styling
  shapeType: 'square', // 'square' or 'circle'
  
  // Error correction
  errorCorrectionLevel: 'Q',
};

/**
 * Metallic color presets with gradient definitions
 */
export const METALLIC_COLORS = {
  gold: {
    name: 'Gold',
    type: 'linear',
    rotation: 45,
    colors: [
      { offset: 0, color: '#BF953F' },
      { offset: 0.25, color: '#FCF6BA' },
      { offset: 0.5, color: '#B38728' },
      { offset: 0.75, color: '#FBF5B7' },
      { offset: 1, color: '#AA771C' },
    ],
  },
  silver: {
    name: 'Silver',
    type: 'linear',
    rotation: 45,
    colors: [
      { offset: 0, color: '#C0C0C0' },
      { offset: 0.25, color: '#E8E8E8' },
      { offset: 0.5, color: '#A8A8A8' },
      { offset: 0.75, color: '#E0E0E0' },
      { offset: 1, color: '#909090' },
    ],
  },
  bronze: {
    name: 'Bronze',
    type: 'linear',
    rotation: 45,
    colors: [
      { offset: 0, color: '#CD7F32' },
      { offset: 0.25, color: '#E8C496' },
      { offset: 0.5, color: '#B87333' },
      { offset: 0.75, color: '#DAA06D' },
      { offset: 1, color: '#A0522D' },
    ],
  },
  rosegold: {
    name: 'Rose Gold',
    type: 'linear',
    rotation: 45,
    colors: [
      { offset: 0, color: '#B76E79' },
      { offset: 0.25, color: '#EACBD2' },
      { offset: 0.5, color: '#C9A0A0' },
      { offset: 0.75, color: '#E8C4C4' },
      { offset: 1, color: '#9E5A63' },
    ],
  },
  chrome: {
    name: 'Chrome',
    type: 'linear',
    rotation: 90,
    colors: [
      { offset: 0, color: '#E8E8E8' },
      { offset: 0.3, color: '#F5F5F5' },
      { offset: 0.5, color: '#CCCCCC' },
      { offset: 0.7, color: '#F0F0F0' },
      { offset: 1, color: '#D0D0D0' },
    ],
  },
  holographic: {
    name: 'Holographic',
    type: 'linear',
    rotation: 135,
    colors: [
      { offset: 0, color: '#FF6B6B' },
      { offset: 0.2, color: '#FFE66D' },
      { offset: 0.4, color: '#4ECDC4' },
      { offset: 0.6, color: '#45B7D1' },
      { offset: 0.8, color: '#96E6A1' },
      { offset: 1, color: '#DDA0DD' },
    ],
  },
};

/**
 * Presets for common use cases
 */
export const QR_PRESETS = {
  default: {
    name: 'Classic',
    description: 'Clean and simple design',
    config: { ...DEFAULT_QR_CONFIG },
  },
  social: {
    name: 'Social Media',
    description: 'Perfect for social profiles',
    config: {
      ...DEFAULT_QR_CONFIG,
      dotsType: 'dots',
      dotsColor: '#1DA1F2',
      cornersSquareType: 'dot',
      cornersSquareColor: '#1DA1F2',
      cornersDotColor: '#1DA1F2',
    },
  },
  business: {
    name: 'Business Card',
    description: 'Professional and elegant',
    config: {
      ...DEFAULT_QR_CONFIG,
      dotsType: 'classy',
      dotsColor: '#1e293b',
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#1e293b',
      cornersDotColor: '#1e293b',
    },
  },
  payment: {
    name: 'Payment Link',
    description: 'High contrast for scanning',
    config: {
      ...DEFAULT_QR_CONFIG,
      dotsType: 'square',
      errorCorrectionLevel: 'H',
      margin: 15,
    },
  },
  creative: {
    name: 'Creative',
    description: 'Colorful gradient style',
    config: {
      ...DEFAULT_QR_CONFIG,
      dotsType: 'extra-rounded',
      dotsColor: '#8b5cf6',
      cornersSquareType: 'extra-rounded',
      cornersSquareColor: '#ec4899',
      cornersDotType: 'dot',
      cornersDotColor: '#8b5cf6',
    },
  },
  minimal: {
    name: 'Minimal',
    description: 'Ultra clean and modern',
    config: {
      ...DEFAULT_QR_CONFIG,
      dotsType: 'rounded',
      dotsColor: '#374151',
      cornersSquareType: 'square',
      cornersSquareColor: '#374151',
      cornersDotType: 'square',
      cornersDotColor: '#374151',
      margin: 20,
    },
  },
};

// Context for managing QR state across components
const QRContext = createContext(null);

/**
 * QR Provider Component
 * Manages global QR code state and saved designs
 */
export function QRProvider({ children }) {
  const [qrConfig, setQRConfig] = useState(DEFAULT_QR_CONFIG);
  const [savedDesigns, setSavedDesigns] = useState(() => {
    // Load saved designs from localStorage
    try {
      const saved = localStorage.getItem('qr-it-designs');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Update a single config property
  const updateConfig = useCallback((key, value) => {
    setQRConfig(prev => ({ ...prev, [key]: value }));
  }, []);

  // Update multiple config properties at once
  const updateConfigBatch = useCallback((updates) => {
    setQRConfig(prev => ({ ...prev, ...updates }));
  }, []);

  // Reset to default config
  const resetConfig = useCallback(() => {
    setQRConfig(DEFAULT_QR_CONFIG);
  }, []);

  // Apply a preset
  const applyPreset = useCallback((presetKey) => {
    const preset = QR_PRESETS[presetKey];
    if (preset) {
      setQRConfig(prev => ({
        ...prev,
        ...preset.config,
        data: prev.data, // Keep the current data
      }));
    }
  }, []);

  // Save current design
  const saveDesign = useCallback((name) => {
    const design = {
      id: uuidv4(),
      name: name || `Design ${savedDesigns.length + 1}`,
      config: { ...qrConfig },
      createdAt: new Date().toISOString(),
    };
    
    const updated = [design, ...savedDesigns];
    setSavedDesigns(updated);
    localStorage.setItem('qr-it-designs', JSON.stringify(updated));
    return design;
  }, [qrConfig, savedDesigns]);

  // Load a saved design
  const loadDesign = useCallback((designId) => {
    const design = savedDesigns.find(d => d.id === designId);
    if (design) {
      setQRConfig(design.config);
    }
  }, [savedDesigns]);

  // Delete a saved design
  const deleteDesign = useCallback((designId) => {
    const updated = savedDesigns.filter(d => d.id !== designId);
    setSavedDesigns(updated);
    localStorage.setItem('qr-it-designs', JSON.stringify(updated));
  }, [savedDesigns]);

  // Generate shareable link (encodes config in URL)
  const getShareableLink = useCallback(() => {
    const configString = btoa(JSON.stringify(qrConfig));
    return `${window.location.origin}/generator?config=${configString}`;
  }, [qrConfig]);

  // Load config from URL
  const loadFromShareableLink = useCallback((configString) => {
    try {
      const config = JSON.parse(atob(configString));
      setQRConfig({ ...DEFAULT_QR_CONFIG, ...config });
      return true;
    } catch {
      return false;
    }
  }, []);

  const value = {
    qrConfig,
    setQRConfig,
    updateConfig,
    updateConfigBatch,
    resetConfig,
    applyPreset,
    savedDesigns,
    saveDesign,
    loadDesign,
    deleteDesign,
    getShareableLink,
    loadFromShareableLink,
  };

  return (
    <QRContext.Provider value={value}>
      {children}
    </QRContext.Provider>
  );
}

/**
 * Hook to access QR context
 */
export function useQR() {
  const context = useContext(QRContext);
  if (!context) {
    throw new Error('useQR must be used within a QRProvider');
  }
  return context;
}

export default useQR;
